import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { Loan, LoanTableEntry } from '@shared/definitions/loan';
import { 
  CashflowEntry, 
  CashflowLoanPayment 
} from '@shared/definitions/financial/cashflow.model';
import { 
  DatePeriod} from '@shared/definitions/financial/time-period.model';
import { 
  TimePeriod 
} from '@shared/definitions/financial/time-period.model';
import { TimePeriodService } from '@core/services/finance/time-period.service';
import { toDate } from '../../../shared/utils/to-date';

/**
 * Service responsible for adapting Loan models to standardized financial models
 * This service bridges between the Loan data model and the financial calculation services
 */
@Injectable({
  providedIn: 'root'
})
export class LoanAdapterService {
  constructor(private timePeriodService: TimePeriodService) {}

  /**
   * Convert a loan to a series of cashflow entries
   * @param loan The loan to convert
   * @param period The overall date period to consider
   * @param targetPeriodType The desired period type for the result
   * @returns Observable of cashflow entries
   */
  convertLoanToCashflow(
    loan: Loan, 
    period: DatePeriod,
    targetPeriodType: TimePeriod = TimePeriod.MONTHLY
  ): Observable<CashflowEntry[]> {
    // console.log(`[LOAN_FLOW] Starting loan to cashflow conversion for loan ${loan?.loanUID || 'unknown'}`);
    
    if (!loan || !loan.loanTable || loan.loanTable.length === 0) {
      // console.log(`[LOAN_FLOW] No loan table available for conversion, returning empty array`);
      return of([]);
    }

    try {
      
      // Create daily cashflow entries from loan table
      const dailyEntries = this.createDailyEntriesFromLoan(loan, period);
      
      // Convert to target period type if not daily
      if (targetPeriodType !== TimePeriod.DAILY) {
        return of(this.timePeriodService.convertCashflowPeriod(dailyEntries, targetPeriodType))
          .pipe(
            tap(result => console.log(`[LOAN_FLOW] Conversion complete: ${result.length} ${targetPeriodType} entries for loan ${loan.loanUID}`))
          );
      }
      
      return of(dailyEntries)
        .pipe(
          tap(result => console.log(`[LOAN_FLOW] Daily entries returned: ${result.length} entries for loan ${loan.loanUID}`))
        );
    } catch (error) {
      console.error('[LOAN_FLOW] Error converting loan to cashflow:', error);
      return of([]);
    }
  }

  /**
   * Get loan payments breakdown for a specific period
   * @param loan The loan to analyze
   * @param period The date period to get payments for
   * @returns Loan payment details for the period
   */
  getLoanPaymentsForPeriod(loan: Loan, period: DatePeriod): CashflowLoanPayment {
    // Debug loan input to identify NULL or undefined values
    console.log(`[LOAN_FLOW] Processing loan payment for period:`, {
      loanUID: loan?.loanUID || 'UNKNOWN',
      loanTableLength: loan?.loanTable?.length || 0,
      loanHasTable: !!loan?.loanTable,
      periodStart: period?.startDate?.toISOString() || 'INVALID_DATE',
      periodEnd: period?.endDate?.toISOString() || 'INVALID_DATE'
    });
    
    if (!loan || !loan.loanTable || loan.loanTable.length === 0) {
      return this.createEmptyLoanPayment(loan);
    }

    try {
      // Create a cache key for this loan + period combination to avoid redundant calculations
      const cacheKey = `${loan.loanUID}_${period.type}_${period.startDate.getTime()}_${period.endDate.getTime()}`;
      
      // Check if we have this calculation in a class-level cache (implement this cache at the class level)
      if (this._paymentCache && this._paymentCache.has(cacheKey)) {
        const cachedResult = this._paymentCache.get(cacheKey)!;
        return cachedResult;
      }
      
      // Performance check for large loan tables
      
      // Debug first and last entry in loan table
      if (loan.loanTable.length > 0) {
        const firstDate = new Date(loan.loanTable[0].date);
        console.log(`[LOAN_FLOW] First loan table entry:`, {
          date: firstDate.toISOString(),
          dateValid: !isNaN(firstDate.getTime()),
          principal: loan.loanTable[0].principalRepayment,
          principalValid: !isNaN(loan.loanTable[0].principalRepayment),
          interest: loan.loanTable[0].interest,
          interestValid: !isNaN(loan.loanTable[0].interest),
          fees: loan.loanTable[0].fees,
          feesValid: !isNaN(loan.loanTable[0].fees || 0)
        });
        
        const lastEntry = loan.loanTable[loan.loanTable.length - 1];
        const lastDate = new Date(lastEntry.date);
        console.log(`[LOAN_FLOW] Last loan table entry:`, {
          date: lastDate.toISOString(),
          dateValid: !isNaN(lastDate.getTime()),
          principal: lastEntry.principalRepayment,
          principalValid: !isNaN(lastEntry.principalRepayment),
          interest: lastEntry.interest,
          interestValid: !isNaN(lastEntry.interest),
          fees: lastEntry.fees,
          feesValid: !isNaN(lastEntry.fees || 0)
        });
      }
      
      // Use more efficient filtering approach for large tables
      let relevantEntries: LoanTableEntry[] = [];

      // Short-circuit: If period is yearly and we have many entries, use year-based filtering first
      if (period.type === TimePeriod.YEARLY && loan.loanTable.length > 50) {
        const yearStart = period.startDate.getFullYear();
        const yearEnd = period.endDate.getFullYear();
        
        
        // Pre-filter by year to reduce the set we need to check precisely
        relevantEntries = loan.loanTable.filter((entry: LoanTableEntry) => {
          // Check if entry.date is valid before processing
          if (!entry.date) {
            console.warn(`[LOAN_FLOW] Entry missing date in loan ${loan.loanUID}`);
            return false;
          }
          
          try {
            const entryDate = new Date(entry.date);
            if (isNaN(entryDate.getTime())) {
              console.warn(`[LOAN_FLOW] Invalid date in loan ${loan.loanUID}: ${entry.date}`);
              return false;
            }
            
            const entryYear = entryDate.getFullYear();
            return entryYear >= yearStart && entryYear <= yearEnd;
          } catch (error) {
            console.error(`[LOAN_FLOW] Error processing date in loan ${loan.loanUID}:`, error);
            return false;
          }
        });
      } else {
        // Standard filtering for smaller tables or non-yearly periods
        relevantEntries = loan.loanTable.filter((entry: LoanTableEntry) => 
          this.entryOverlapsPeriod(entry, period)
        );
      }
      
      // Debug filtered entries
      if (relevantEntries.length > 0) {
        // Check for invalid values in filtered entries
        const invalidValueEntries = relevantEntries.filter((entry: LoanTableEntry) => 
          isNaN(entry.principalRepayment) || 
          isNaN(entry.interest) || 
          (entry.fees !== undefined && isNaN(entry.fees))
        );
        
        if (invalidValueEntries.length > 0) {
          console.warn(`[LOAN_FLOW] Found ${invalidValueEntries.length} entries with invalid values in filtered set for loan ${loan.loanUID}`);
          console.warn(`[LOAN_FLOW] First invalid entry:`, invalidValueEntries[0]);
        }
      }
      
      if (relevantEntries.length === 0) {
        const emptyPayment = this.createEmptyLoanPayment(loan);
        // Cache the empty result too
        if (!this._paymentCache) this._paymentCache = new Map();
        this._paymentCache.set(cacheKey, emptyPayment);
        
        return emptyPayment;
      }
      
      // Calculate total payments, principal, and interest
      let totalPayment = 0;
      let totalPrincipal = 0;
      let totalInterest = 0;

      relevantEntries.forEach((entry: LoanTableEntry) => {
        // Check for valid numbers to prevent NaN
        const principalValue = typeof entry.principalRepayment === 'number' ? entry.principalRepayment : 0;
        const interestValue = typeof entry.interest === 'number' ? entry.interest : 0;
        const feesValue = typeof entry.fees === 'number' ? entry.fees : 0;

        if (isNaN(principalValue) || isNaN(interestValue) || isNaN(feesValue)) {
          console.warn(`[LOAN_FLOW] Found NaN values in entry for date ${new Date(entry.date).toISOString()} in loan ${loan.loanUID}:`, {
            principalRepayment: entry.principalRepayment,
            interest: entry.interest,
            fees: entry.fees
          });
        } else {
          // Add weighted values (optimize by skipping overlap calculation for full periods)
          const overlapRatio = 1; // Assume full overlap for simplicity, or calculate if needed
          totalPrincipal += principalValue * overlapRatio;
          totalInterest += interestValue * overlapRatio;
          totalPayment += (principalValue + interestValue + feesValue) * overlapRatio;
        }
      });


      if (isNaN(totalPayment) || isNaN(totalPrincipal) || isNaN(totalInterest)) {
        console.error(`[LOAN_FLOW] Calculated INVALID payment amounts for loan ${loan.loanUID}:`, {
          totalPayment,
          totalPrincipal,
          totalInterest
        });
        
        // Return zero values instead of NaN
        const emptyPayment = this.createEmptyLoanPayment(loan);
        if (!this._paymentCache) this._paymentCache = new Map();
        this._paymentCache.set(cacheKey, emptyPayment);
        return emptyPayment;
      }
      
      const result = {
        lender: this.getLoanProviderName(loan),
        payment: totalPayment,
        principal: totalPrincipal,
        interest: totalInterest
      };
      
      
      // Cache the result for future use
      if (!this._paymentCache) this._paymentCache = new Map();
      this._paymentCache.set(cacheKey, result);
      
      return result;
    } catch (error) {
      console.error(`[LOAN_FLOW] Error getting loan payments for period for loan ${loan?.loanUID}:`, error);
      return this.createEmptyLoanPayment(loan);
    }
  }

  /**
   * Calculate remaining loan balance at a specific date
   * @param loan The loan to analyze
   * @param date The date to calculate the balance for
   * @returns The remaining balance
   */
  getRemainingBalance(loan: Loan, date: Date): number {
    if (!loan) {
      return 0;
    }
    
    // Check if the date is after the loan end date
    if (loan.startDate && loan.numberOfMonths) {
      const loanEndDate = new Date(loan.startDate.toDate().getTime() + 
        loan.numberOfMonths * 30 * 24 * 60 * 60 * 1000);
      
      if (date > loanEndDate) {
        return 0; // Return 0 for dates after the loan end
      }
    }
    
    // Check if the loan table is empty
    if (!loan.loanTable || loan.loanTable.length === 0) {
      return 0;
    }
    
    return loan.getCapitalRemainingDue(date);
  }

  /**
   * Get all loan payments for a collection of loans during a specific period
   * @param loans Collection of loans to analyze
   * @param period The date period to get payments for
   * @returns Array of loan payments with details
   */
  getAllLoanPaymentsForPeriod(loans: Loan[], period: DatePeriod): CashflowLoanPayment[] {
    // console.log('[DEBUG] LoanAdapterService: Starting getAllLoanPaymentsForPeriod with', loans.length, 'loans'); // Commented out to reduce noise

    if (!loans || loans.length === 0) {
      return [];
    }
    
    try {
      // Filter loans that overlap with the period
      const loansFilterStartTime = Date.now();
      const overlappingLoans = loans.filter((loan: Loan) => this.loanOverlapsPeriod(loan, period));
      const _loansFilterTime = Date.now() - loansFilterStartTime;
      
      // console.log(`[DEBUG] LoanAdapterService: Filtered ${overlappingLoans.length} overlapping loans out of ${loans.length} in ${(loansFilterTime - loansFilterStartTime).toFixed(0)}ms`); // Commented out to reduce noise
      
      // Process each loan
      const results: CashflowLoanPayment[] = [];
      
      // Process each overlapping loan
      for (const loan of overlappingLoans) {
        const loanStartTime = Date.now();
        
        // console.log(`[LOAN_FLOW] Processing loan payment for period:`, { // Commented out to reduce noise
        //   loanUID: loan.loanUID,
        //   loanTableLength: loan.loanTable?.length || 0,
        //   loanHasTable: !!loan.loanTable,
        //   periodStart: period.start.toISOString(),
        //   periodEnd: period.end.toISOString()
        // });

        const payment = this.getLoanPaymentsForPeriod(loan, period);
        if (payment) {
          const _loanProcessTime = Date.now() - loanStartTime;
          // console.log(`[DEBUG] LoanAdapterService: Processed loan ${loan.loanUID} in ${loanProcessTime}ms, payment: ${payment.payment}`); // Commented out to reduce noise
          results.push(payment);
        }
      }
      
      return results;
    } catch (error) {
      console.error(`[DEBUG] LoanAdapterService: Error in getAllLoanPaymentsForPeriod:`, error);
      return [];
    }
  }

  /**
   * Calculate total debt service for a collection of loans during a period
   * @param loans Collection of loans to analyze
   * @param period The date period to calculate for
   * @returns The total debt service amount and detailed payments
   */
  calculateDebtService(
    loans: Loan[],
    period: DatePeriod
  ): { totalDebtService: number; loanPayments: CashflowLoanPayment[] } {
    
    if (!loans || loans.length === 0) {
      return { totalDebtService: 0, loanPayments: [] };
    }
    
    try {
      // Verify loans array integrity
      const validLoans = loans.filter((loan: Loan) => loan && loan.loanUID);
      
      // Log loan details for debugging
      validLoans.forEach((_loan: Loan, _index: number) => {
      });
      
      if (validLoans.length < loans.length) {
        console.warn(`[LOAN_FLOW] Found ${loans.length - validLoans.length} invalid loans in array`);
      }
      
      // Get all loan payments for the period
      const loanPayments = this.getAllLoanPaymentsForPeriod(validLoans, period);
      
      // Log payment details for debugging
      loanPayments.forEach((_payment: CashflowLoanPayment, _index: number) => {
      });
      
      // Calculate total debt service
      const totalDebtService = loanPayments.reduce(
        (sum: number, payment: CashflowLoanPayment) => sum + (payment.payment || 0), 
        0
      );
      
      
      if (isNaN(totalDebtService)) {
        console.error(`[LOAN_FLOW] Total debt service is NaN, returning zero`);
        return { totalDebtService: 0, loanPayments: [] };
      }
      
      return { totalDebtService, loanPayments };
    } catch (error) {
      console.error(`[LOAN_FLOW] Error calculating debt service:`, error);
      return { totalDebtService: 0, loanPayments: [] };
    }
  }

  /**
   * Create basic cashflow entries for a loan
   * @param loan The loan to convert
   * @param period The overall period to consider
   * @returns Array of daily cashflow entries
   */
  private createDailyEntriesFromLoan(loan: Loan, period: DatePeriod): CashflowEntry[] {
    const entries: CashflowEntry[] = [];
    
    // Filter loan table entries that overlap with the period
    const relevantEntries = loan.loanTable.filter(entry => 
      this.entryOverlapsPeriod(entry, period)
    );
    
    // Create a cashflow entry for each loan table entry
    relevantEntries.forEach(entry => {
      const entryDate = new Date(entry.date);
      
      // Skip invalid dates
      if (isNaN(entryDate.getTime())) {
        console.warn(`[LOAN_FLOW] Skipping entry with invalid date in loan ${loan.loanUID}`);
        return;
      }
      
      // Create a day period for this entry
      const entryPeriod: DatePeriod = {
        startDate: new Date(entryDate),
        endDate: new Date(entryDate),
        type: TimePeriod.DAILY,
        label: entryDate.toISOString().split('T')[0]
      };
      
      // Calculate total payment
      const totalPayment = entry.principalRepayment + entry.interest + (entry.fees || 0);
      
      // Create cashflow entry
      const cashflowEntry: CashflowEntry = {
        period: entryPeriod,
        cashIn: 0, // Loan payments are outflows, not inflows
        cashOut: totalPayment,
        cashFlow: -totalPayment, // Negative because it's an outflow
        isProjected: true, // Loan payments are typically projected
        source: {
          type: 'loan',
          id: loan.loanUID,
          displayName: this.getLoanProviderName(loan),
          propertyId: loan.propertyUID
        },
        // Add breakdown of outflows
        cashOutBreakdown: {
          principal: entry.principalRepayment,
          interest: entry.interest,
          fees: entry.fees || 0
        }
      };
      
      entries.push(cashflowEntry);
    });
    
    return entries;
  }

  /**
   * Check if a loan table entry overlaps with a period
   */
  private entryOverlapsPeriod(entry: LoanTableEntry, period: DatePeriod): boolean {
    const entryDate = new Date(entry.date);
    return entryDate >= period.startDate && entryDate <= period.endDate;
  }

  /**
   * Calculate overlap ratio for partial periods
   * For loan entries, this is simplified since they represent points in time
   */
  private calculateEntryOverlapRatio(entry: LoanTableEntry, period: DatePeriod): number {
    // If entry date is within period, it's fully included
    const entryDate = new Date(entry.date);
    if (entryDate >= period.startDate && entryDate <= period.endDate) {
      return 1;
    }
    return 0;
  }

  /**
   * Check if a loan overlaps with a period
   */
  private loanOverlapsPeriod(loan: Loan, period: DatePeriod): boolean {
    const loanStartDate = loan.startDate.toDate();
    const loanEndDate = toDate(loan.getEndDate()) ??
      
      new Date(loanStartDate.getTime() + loan.numberOfMonths * 30 * 24 * 60 * 60 * 1000);
    
    return (loanStartDate <= period.endDate) && (loanEndDate >= period.startDate);
  }

  /**
   * Create an empty loan payment object for a loan
   * @param loan The reference loan
   * @returns Empty loan payment object
   */
  private createEmptyLoanPayment(loan: Loan | null): CashflowLoanPayment {
    return {
      lender: this.getLoanProviderName(loan),
      payment: 0,
      principal: 0,
      interest: 0
    };
  }

  /**
   * Get loan provider name with fallback
   */
  private getLoanProviderName(loan: Loan | null): string {
    if (!loan) {
      return 'Unknown Loan';
    }
    
    // Get the lender name
    const lenderName = loan.loanProvider?.name || 'Unknown Lender';
    
    // Get the interest rate (yield) if available
    const interestRate = loan.interest?.rate;
    
    // Format the display string with lender name and yield
    if (interestRate !== undefined && interestRate !== null) {
      const yieldPercentage = (interestRate * 100).toFixed(2);
      return `${lenderName} (${yieldPercentage}%)`;
    }
    
    // Fallback to just lender name if no interest rate available
    return lenderName;
  }

  // Add a class property for payment caching
  private _paymentCache: Map<string, CashflowLoanPayment> | undefined;
} 
